import { useState, useRef } from "react";
import { fileUrl } from "@/lib/api";
import { X, Sparkles, GripVertical, ShoppingBag, Instagram, Globe2, Star, RotateCw } from "lucide-react";

/**
 * PhotoBoard — drag-and-drop photo reorder grid with per-photo channel toggles.
 *
 * Storage contract:
 *  - `photos` is an ordered array of paths. The FIRST entry is the hero (shop listing thumbnail).
 *  - `photo_channels` is a {path: [channelCodes]} map. Missing entry == enabled on every channel.
 *
 * Props:
 *  - photos: string[]
 *  - photoChannels: {path: string[]}
 *  - enhancingPhotos: Record<string, boolean>   (paths currently being enhanced — supports parallel)
 *  - onReorder(nextPaths)
 *  - onRemove(path)
 *  - onEnhance(path)
 *  - onToggleChannel(path, channel)
 */
const CHANNELS = [
  { code: "shop",  label: "shop",  Icon: ShoppingBag },
  { code: "ig",    label: "ig",    Icon: Instagram },
  { code: "ebay",  label: "ebay",  Icon: Globe2 },
];

export default function PhotoBoard({ photos, photoChannels, photoRoles, enhancingPhotos, rotatingPhoto, onReorder, onRemove, onEnhance, onEnhanceBatch, onRotate, onToggleChannel }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const dragImgRef = useRef(null);

  if (!photos || photos.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-taupe/60" data-testid="photo-board">
        <p className="font-serif text-2xl lowercase text-taupe">no photographs yet.</p>
      </div>
    );
  }

  const isEnabled = (path, code) => {
    const chans = photoChannels?.[path];
    if (chans === undefined) return true; // unmapped = enabled everywhere
    return chans.includes(code);
  };

  const handleDragStart = (e, i) => {
    setDragIdx(i);
    // Use a transparent 1px image so the browser doesn't show its default ghost.
    if (!dragImgRef.current) {
      const img = new Image();
      img.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
      dragImgRef.current = img;
    }
    e.dataTransfer.setDragImage(dragImgRef.current, 0, 0);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, i) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIdx !== i) setOverIdx(i);
  };

  const handleDrop = (e, dropIdx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === dropIdx) {
      setDragIdx(null);
      setOverIdx(null);
      return;
    }
    const next = [...photos];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(dropIdx, 0, moved);
    setDragIdx(null);
    setOverIdx(null);
    onReorder(next);
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    setOverIdx(null);
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="photo-board">
      {photos.map((p, i) => {
        const isHero = i === 0;
        const isDragging = dragIdx === i;
        const isOver = overIdx === i && dragIdx !== null && dragIdx !== i;
        return (
          <div
            key={p}
            draggable
            onDragStart={(e) => handleDragStart(e, i)}
            onDragOver={(e) => handleDragOver(e, i)}
            onDrop={(e) => handleDrop(e, i)}
            onDragEnd={handleDragEnd}
            data-testid={`photo-tile-${i}`}
            className={`relative group bg-ivory border ${isOver ? "border-brass border-2" : "border-taupe/40"} ${isDragging ? "opacity-40" : ""} transition-all`}
          >
            <img src={fileUrl(p)} alt="" className="w-full aspect-square object-cover pointer-events-none" />

            {/* drag handle — explicit affordance, top-left */}
            <div
              className="absolute top-2 left-2 bg-ivory/90 border border-taupe p-1 cursor-grab active:cursor-grabbing"
              title="drag to reorder"
              data-testid={`photo-drag-${i}`}
            >
              <GripVertical className="w-3 h-3 text-charcoal" />
            </div>

            {/* hero badge on the first photo */}
            {isHero && (
              <span
                className="absolute top-2 left-1/2 -translate-x-1/2 bg-brass text-ivory text-[0.55rem] tracking-widest uppercase px-2 py-0.5 inline-flex items-center gap-1"
                data-testid="photo-hero-badge"
              >
                <Star className="w-2.5 h-2.5" /> hero
              </span>
            )}

            <button
              onClick={() => onRemove(p)}
              className="absolute top-2 right-2 bg-ivory/90 p-1.5 border border-taupe hover:border-red-700"
              aria-label="remove photo"
              data-testid={`photo-remove-${i}`}
            >
              <X className="w-3 h-3" />
            </button>

            {/* rotate 90° clockwise — server-side, persists across all channels */}
            {onRotate && (
              <button
                onClick={() => onRotate(p)}
                disabled={rotatingPhoto === p}
                className="absolute top-12 left-2 bg-ivory/90 p-1.5 border border-taupe hover:border-brass disabled:opacity-50"
                title="rotate 90° clockwise"
                aria-label="rotate photo"
                data-testid={`photo-rotate-${i}`}
              >
                <RotateCw className={`w-3 h-3 ${rotatingPhoto === p ? "animate-spin" : ""}`} />
              </button>
            )}

            {/* channel toggles — slight inset so they don't collide with the enhance bar at the bottom */}
            <div className="absolute top-9 right-2 bottom-12 flex flex-col gap-1 justify-start">
              {CHANNELS.map(({ code, Icon }) => {
                const on = isEnabled(p, code);
                return (
                  <button
                    key={code}
                    onClick={() => onToggleChannel(p, code)}
                    title={`${code} ${on ? "enabled" : "disabled"}`}
                    data-testid={`photo-ch-${i}-${code}`}
                    className={`p-1.5 border ${on ? "bg-brass text-ivory border-brass" : "bg-ivory/90 text-taupe border-taupe"}`}
                  >
                    <Icon className="w-3 h-3" />
                  </button>
                );
              })}
            </div>

            {/* role badge — top-right, tucked under the remove X */}
            {photoRoles?.[p] && photoRoles[p] !== "studio" && (
              <span
                className={`absolute top-9 left-2 text-[0.55rem] tracking-widest uppercase px-1.5 py-0.5 border ${
                  photoRoles[p] === "sourcing" ? "bg-taupe/20 text-espresso border-taupe" :
                  photoRoles[p] === "condition" ? "bg-red-100 text-red-800 border-red-800/40" :
                  photoRoles[p] === "detail" ? "bg-brass/20 text-brass border-brass/40" :
                  "bg-ivory/90 text-charcoal border-taupe"
                }`}
                data-testid={`photo-role-${i}`}
                title={photoRoles[p] === "sourcing" ? "internal · never publishes" : photoRoles[p] === "condition" ? "internal · condition record" : photoRoles[p]}
              >
                {photoRoles[p]}
              </span>
            )}

            <div className="absolute bottom-2 left-2 right-2 flex gap-1">
              <button
                onClick={() => onEnhance(p)}
                disabled={!!enhancingPhotos?.[p]}
                data-testid={`photo-enhance-${i}`}
                className="flex-1 bg-charcoal/90 hover:bg-brass text-ivory text-[0.65rem] tracking-widest uppercase py-2 flex items-center justify-center gap-1 transition-colors disabled:opacity-70"
              >
                <Sparkles className={`w-3 h-3 ${enhancingPhotos?.[p] ? "animate-pulse" : ""}`} /> {enhancingPhotos?.[p] ? "…" : "enhance"}
              </button>
              {onEnhanceBatch && (
                <button
                  onClick={() => onEnhanceBatch(p)}
                  disabled={!!enhancingPhotos?.[p]}
                  data-testid={`photo-enhance-batch-${i}`}
                  className="bg-brass/90 hover:bg-espresso text-ivory text-[0.65rem] tracking-widest uppercase py-2 px-2 transition-colors disabled:opacity-70"
                  title="editorial batch: hero + lifestyle + studio in parallel"
                >
                  ×3
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
